import { useChat } from "@ai-sdk/react";
import { useCallback, useEffect, useState } from "react";
import type { ChatMessage } from "../components/ChatBox/Messages";
import { createApiUrl } from "../config/api";
import type { DataResponse } from "../types/dataResponse";

interface UseChatSessionProps {
  sessionId: string | null;
  userID: string | null;
}

export function useChatSession({ sessionId, userID }: UseChatSessionProps) {
  const [isInitialized, setIsInitialized] = useState(false);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [customStatusMessage, setCustomStatusMessage] = useState<string | null>(null);
  const [hasMessageLimitError, setHasMessageLimitError] = useState(false);

  // Initialize the AI SDK chat
  const chat = useChat({
    api: createApiUrl("/chat/chat_streaming"),
    body: {
      session_id: sessionId,
      user_id: userID,
    },
    onError: (error) => {
      console.error('Chat error:', error);
      // The error object from useChat contains the response
      // Check if it's a 507 error (message limit reached)
      const errorStr = error.toString();
      if (errorStr.includes('507') || errorStr.toLowerCase().includes('limit reached')) {
        setHasMessageLimitError(true);
      }
    },
  });

  const { messages, data, setMessages, status } = chat;

  // Reset chat state when sessionId changes
  useEffect(() => {
    setChatMessages([]);
    setCustomStatusMessage(null);
    setHasMessageLimitError(false);
    setMessages([]); // Clear the AI SDK messages
  }, [sessionId, setMessages]);

  // Process data for custom status messages from backend
  useEffect(() => {
    if (Array.isArray(data) && data.length > 0) {
      // Check for status responses in the data
      const statusItems = data.filter(item => 
        item && 
        typeof item === 'object' && 
        item !== null && 
        'type' in item &&
        item.type === 'status'
      );
      
      if (statusItems.length > 0) {
        // Show the most recent status message
        const lastStatus = statusItems[statusItems.length - 1];
        setCustomStatusMessage((lastStatus as any).data?.payload || (lastStatus as any).data?.status);
      }
    }
  }, [data]);

  // Clear custom status when streaming stops or when actual message content starts
  useEffect(() => {
    if (status === 'ready') {
      // Clear custom status when response is complete
      setCustomStatusMessage(null);
    } else if (messages.length > 0 && status === 'streaming') {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.role === 'assistant' && lastMessage.content && lastMessage.content.trim()) {
        // Assistant message has content, clear custom status
        setCustomStatusMessage(null);
      }
    }
  }, [status, messages]);

  // Mark as initialized when we have session info
  useEffect(() => {
    if (sessionId && userID && !isInitialized) {
      setIsInitialized(true);
    }
  }, [sessionId, userID, isInitialized]);

  // Convert useChat messages to our ChatMessage format
  // Preserve user messages with attachments, convert AI messages directly
  useEffect(() => {
    const convertedMessages: ChatMessage[] = messages.map(msg => {
      // Check if we already have this user message with attachments
      const existingUserMsg = chatMessages.find(
        existing => existing.role === 'user' && 
                   existing.content === msg.content && 
                   existing.attachment && 
                   existing.attachment.length > 0
      );
      
      // If user message with attachments exists, keep it
      if (msg.role === 'user' && existingUserMsg) {
        return existingUserMsg;
      }
      
      // Otherwise convert normally (AI messages never have attachments)
      return {
        role: msg.role as 'user' | 'assistant',
        content: msg.content,
        id: msg.id,
        createdAt: msg.createdAt ? new Date(msg.createdAt) : new Date()
        // No attachment field for AI messages
      };
    });

    setChatMessages(convertedMessages);
  }, [messages]);

  // Restore preserved messages
  const restoreMessages = useCallback((preservedMessages: ChatMessage[]) => {
    if (preservedMessages && preservedMessages.length > 0) {
      setChatMessages(prev => {
        // Find new messages that aren't already in our chat
        const existingIds = new Set(prev.map(msg => msg.id));
        const newMessages = preservedMessages.filter(msg => 
          msg.id && !existingIds.has(msg.id)
        );
        
        if (newMessages.length > 0) {
          // Add new messages and sort by creation time
          const combined = [...prev, ...newMessages];
          return combined.sort((a, b) => {
            const timeA = a.createdAt ? a.createdAt.getTime() : Date.now();
            const timeB = b.createdAt ? b.createdAt.getTime() : Date.now();
            return timeA - timeB;
          });
        }
        return prev;
      });

      // Convert AI messages to useChat format (excluding user messages with attachments)
      const existingIds = new Set(messages.map(msg => msg.id));
      const newAiMessages = preservedMessages
        .filter(msg => msg.role === 'assistant' && msg.id && !existingIds.has(msg.id))
        .map(msg => ({
          role: msg.role,
          content: msg.content,
          id: msg.id,
          createdAt: msg.createdAt
        }));
      
      if (newAiMessages.length > 0) {
        setMessages(prev => {
          const combined = [...prev, ...newAiMessages as any];
          return combined.sort((a, b) => {
            const timeA = a.createdAt ? a.createdAt.getTime() : Date.now();
            const timeB = b.createdAt ? b.createdAt.getTime() : Date.now();
            return timeA - timeB;
          });
        });
      }
    }
  }, [setMessages, messages]);

  // Add user message with attachments
  const addUserMessageWithAttachments = useCallback((message: ChatMessage) => {
    setChatMessages(prev => [...prev, message]);
  }, []);

  // Add external message (for training updates, etc.)
  const addExternalMessage = useCallback((message: ChatMessage) => {
    setChatMessages(prev => {
      // Check if message already exists
      const existingIndex = prev.findIndex(existing => existing.id === message.id);
      
      if (existingIndex >= 0) {
        // Update existing message
        const updated = [...prev];
        updated[existingIndex] = message;
        return updated;
      } else {
        // Add new message and sort by creation time
        const combined = [...prev, message];
        return combined.sort((a, b) => {
          const timeA = a.createdAt ? a.createdAt.getTime() : Date.now();
          const timeB = b.createdAt ? b.createdAt.getTime() : Date.now();
          return timeA - timeB;
        });
      }
    });

    // If it's an AI message, also add to useChat messages
    if (message.role === 'assistant') {
      setMessages(prev => {
        const existingIndex = prev.findIndex(existing => existing.id === message.id);
        const aiMessage = {
          role: message.role,
          content: message.content,
          id: message.id,
          createdAt: message.createdAt
        };
        
        if (existingIndex >= 0) {
          // Update existing message
          const updated = [...prev];
          updated[existingIndex] = aiMessage as any;
          return updated;
        } else {
          // Add new message
          return [...prev, aiMessage as any];
        }
      });
    }
  }, [setMessages]);

  // Process pending message
  const processPendingMessage = useCallback((
    pendingMessage: ChatMessage,
    onFileUploadWithMessage?: (message: string) => void,
    onPendingMessageProcessed?: () => void
  ) => {
    if (!pendingMessage?.content?.trim()) return;

    // If there are attachments, handle file upload but DON'T trigger streaming
    if (pendingMessage.attachment && pendingMessage.attachment.length > 0) {
      // Add user message with attachments to chat
      addUserMessageWithAttachments(pendingMessage);
      
      if (onFileUploadWithMessage) {
        onFileUploadWithMessage(pendingMessage.content);
      }
      
      //onPendingMessageProcessed?.();
    }
    
    const changeEvent = {
      target: { value: pendingMessage.content },
    } as React.ChangeEvent<HTMLTextAreaElement>;
    
    chat.handleInputChange(changeEvent);
    
    const syntheticEvent = {
      preventDefault: () => {},
    } as React.FormEvent<HTMLFormElement>;
    
    chat.handleSubmit(syntheticEvent);
    
    onPendingMessageProcessed?.();
  }, [chat.handleInputChange, chat.handleSubmit, addUserMessageWithAttachments]);

  // Centralized submit handler - simplified
  const handleCustomSubmit = useCallback(async (
    e: React.FormEvent,
    inputValue: string,
    attachedFiles?: File[],
    uploadFiles?: (message: string) => Promise<{ success: boolean; data?: any }>,
    clearFiles?: () => void,
    onResponse?: (data: any[]) => void
  ) => {
    e.preventDefault();
    
    if (!inputValue.trim()) return;

    if (status === 'streaming' || status === 'submitted') {
      console.log('Skipping submit - message already being processed');
      return;
    }

    // If there are files, handle upload
    if (attachedFiles && attachedFiles.length > 0 && uploadFiles) {
      const userMessage: ChatMessage = {
        role: 'user',
        content: inputValue,
        id: `user-${Date.now()}`,
        attachment: [...attachedFiles],
        createdAt: new Date()
      };
      
      // Add user message with attachments to chat
      addUserMessageWithAttachments(userMessage);

      const clearInputEvent = {
        target: { value: '' },
      } as React.ChangeEvent<HTMLTextAreaElement>;
      chat.handleInputChange(clearInputEvent);
      clearFiles?.();
      
      const result = await uploadFiles(inputValue);
      if (result.success) {
        onResponse?.(result.data || []);
      }
    }
    
    // For text-only messages, trigger streaming
    const changeEvent = {
      target: { value: inputValue },
    } as React.ChangeEvent<HTMLTextAreaElement>;
    
    chat.handleInputChange(changeEvent);
    
    const syntheticEvent = {
      preventDefault: () => {},
    } as React.FormEvent<HTMLFormElement>;
    
    chat.handleSubmit(syntheticEvent);
  }, [chat.handleInputChange, chat.handleSubmit, addUserMessageWithAttachments, status]);

  return {
    // Core chat functionality
    ...chat,
    
    // Our processed messages (includes attachments for user messages)
    chatMessages,
    setChatMessages,
    
    // State
    isInitialized,
    status, // Native useChat status: 'submitted' | 'streaming' | 'ready' | 'error'
    customStatusMessage, // Backend status messages (for agent processing updates)
    hasMessageLimitError,
    data: data as DataResponse[] | undefined,
    sessionId,
    userID,
    
    // Methods
    restoreMessages,
    processPendingMessage,
    addUserMessageWithAttachments,
    addExternalMessage,
    handleCustomSubmit,
  };
}
